// NotificationBell.tsx
// Unread notification badge + dropdown for NavBar

import React, { FC, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Text } from '@chakra-ui/react';
import { UserAuth } from '../../AuthContext';
import NotificationService, { PCNotification } from '../../services/notifications/NotificationService';

const notifService = new NotificationService();

const TYPE_ICONS: Record<string, string> = {
  dealer_invite_received: '🟡',
  dealer_accepted:        '✅',
  dealer_declined:        '❌',
  dealer_standby:         '🟡',
  dealer_cancelled:       '⚠️',
  dealer_timed_out:       '⏰',
  standby_activated:      '⚡',
  standby_released:       '✓',
  event_no_dealer:        '🚨',
  dealer_rated:           '⭐',
};

const NotificationBell: FC = () => {
  const { user } = UserAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<PCNotification[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user?.uid) return;
    const load = async () => {
      const notifs = await notifService.getAll(user.uid);
      setNotifications(notifs.slice(0, 20));
    };
    load();
    const interval = setInterval(load, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, [user?.uid]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markRead = async (n: PCNotification) => {
    if (!user?.uid || !n.id || n.read) return;
    await notifService.markRead(user.uid, n.id);
    setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
    if (n.eventId) navigate(`/event/${n.eventId}`);
  };

  const markAllRead = async () => {
    if (!user?.uid) return;
    await notifService.markAllRead(user.uid);
    setNotifications(prev => prev.map(x => ({ ...x, read: true })));
  };

  if (!user) return null;

  const C = { card: '#131720', border: '#1E2330', white: '#F0F4FF', ink: '#C8D0E0', dim: '#6B7590', gold: '#D4A017', red: '#EF4444' };
  const F = { ui: "'Rajdhani','Arial',sans-serif", mono: "'Share Tech Mono',monospace" };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px',
          position: 'relative', fontSize: 18, lineHeight: 1 }}
        aria-label="Notifications"
      >
        🔔
        {unreadCount > 0 && (
          <span style={{ position: 'absolute', top: 0, right: 0, background: C.red,
            color: '#fff', borderRadius: '50%', width: 16, height: 16, fontSize: 10,
            fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: F.mono }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{ position: 'absolute', right: 0, top: '110%', width: 340,
          background: C.card, border: `1px solid ${C.border}`, borderRadius: 10,
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)', zIndex: 9999, overflow: 'hidden' }}>

          {/* Header */}
          <Flex px={3} py={2} align="center" justify="space-between"
            style={{ borderBottom: `1px solid ${C.border}`, background: '#0D1520' }}>
            <Text style={{ fontFamily: F.mono, fontSize: 11, color: C.gold, letterSpacing: '0.2em' }}>
              NOTIFICATIONS {unreadCount > 0 ? `· ${unreadCount} NEW` : ''}
            </Text>
            {unreadCount > 0 && (
              <button onClick={markAllRead} style={{ background: 'none', border: 'none',
                color: C.dim, fontSize: 11, fontFamily: F.mono, cursor: 'pointer',
                letterSpacing: '0.1em' }}>
                MARK ALL READ
              </button>
            )}
          </Flex>

          {/* List */}
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <Box px={4} py={6} textAlign="center">
                <Text style={{ color: C.dim, fontSize: 13, fontFamily: F.ui }}>No notifications yet</Text>
              </Box>
            ) : (
              notifications.map(n => (
                <div key={n.id}
                  onClick={() => markRead(n)}
                  style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`,
                    background: n.read ? C.card : n.urgent ? '#1A0800' : '#0D1520',
                    cursor: n.eventId ? 'pointer' : 'default',
                    borderLeft: `3px solid ${n.read ? 'transparent' : n.urgent ? '#EF4444' : '#D4A017'}` }}>
                  <Flex align="flex-start" gap={2}>
                    <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>
                      {TYPE_ICONS[n.type] ?? '🔔'}
                    </span>
                    <div style={{ flex: 1 }}>
                      <Text style={{ fontFamily: F.ui, fontSize: 13, color: n.read ? C.dim : C.white,
                        lineHeight: 1.4, marginBottom: 2 }}>
                        {n.message}
                      </Text>
                      <Text style={{ fontFamily: F.mono, fontSize: 10, color: C.dim, letterSpacing: '0.1em' }}>
                        {n.eventName}
                        {n.createdAt?.toDate ? ` · ${n.createdAt.toDate().toLocaleDateString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}
                      </Text>
                    </div>
                    {!n.read && (
                      <div style={{ width: 8, height: 8, borderRadius: '50%',
                        background: n.urgent ? '#EF4444' : '#D4A017', flexShrink: 0, marginTop: 4 }} />
                    )}
                  </Flex>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
